/**
 * One rule for dollar amounts people type: what a string means, and whether
 * the result is believable for the field it's going into.
 *
 * Why this exists: on 2026-09-29, 4 of 21 buyers who registered on the
 * website had typed their minimum EBITDA in the wrong unit, in both
 * directions. The form said "($000s)"; one buyer typed 1 meaning $1M and got
 * $1,000, another typed 1000000 meaning $1M and got $1 billion. Inside the
 * CRM the same amount was entered in thousands (Add Buyer), millions (one-
 * pager import) and raw dollars (newsletter segments). A label is not enough.
 * So every amount gets parsed by the same rules, echoed back as the exact
 * dollars that will be saved, and checked against a plausible range.
 *
 * Typed strings follow formatDealValue's rules: a K/M/B suffix is explicit,
 * a bare number under 1,000 means millions ("5" is $5M), and 1,000 or more
 * is already dollars. A JS number is always dollars, because it comes from
 * code (a parsed input or a database column), never from a person's typing.
 *
 * Never guesses a fix. `suggestion` is set only when exactly one unit
 * reading (x1000, /1000, x1e6, /1e6) lands inside the range.
 */
import { formatDealValue } from "./formatDealValue";

/**
 * Plausible bounds, inclusive, in dollars. Set from live data on 2026-09-29:
 * real buyer EBITDA floors run $250K to $5M, lender minimums $1M to $4M.
 * Widen a bound when a real value is refused, rather than bypassing the check.
 */
export const MONEY_RANGES = {
  buyerEbitda: { min: 100_000, max: 250_000_000, label: "a buyer's EBITDA target" },
  revenue: { min: 500_000, max: 2_000_000_000, label: "revenue" },
  dealSize: { min: 500_000, max: 5_000_000_000, label: "a deal size" },
  lenderEbitda: { min: 100_000, max: 100_000_000, label: "a lender's minimum EBITDA" },
} as const;

export type MoneyKind = keyof typeof MONEY_RANGES;

export interface ParsedMoney {
  status: "empty" | "invalid" | "ok" | "implausible";
  /** Whole dollars. Set for "ok" and "implausible", null otherwise. */
  dollars: number | null;
  /** Compact form, "$1.5M". */
  display: string | null;
  /** Exact form, "$1,500,000": what will actually be saved. */
  full: string | null;
  suggestion: { dollars: number; display: string } | null;
  /** Human sentence for a form hint or a 400 body. Null when "ok"/"empty". */
  message: string | null;
}

const EMPTY: ParsedMoney = { status: "empty", dollars: null, display: null, full: null, suggestion: null, message: null };

const INVALID: ParsedMoney = {
  ...EMPTY,
  status: "invalid",
  message: "Enter one amount, like 500K, 2.5M or 2,500,000.",
};

const MULTIPLIER = { K: 1_000, M: 1_000_000, B: 1_000_000_000 } as const;

function compact(dollars: number): string {
  return formatDealValue(String(dollars));
}

function exact(dollars: number): string {
  return `$${dollars.toLocaleString("en-US")}`;
}

function parseTyped(raw: string): number | null {
  const words = raw
    .replace(/\s*\b(billion|bn)\b/gi, "B")
    .replace(/\s*\b(million|mill|mil|mm)\b/gi, "M")
    .replace(/(\d)\s*(mm)\b/gi, "$1M")
    .replace(/\s*\bthousand\b/gi, "K");
  const cleaned = words.replace(/[$,\s]/g, "").toUpperCase();
  // A dash anywhere is a range or a negative; neither is one amount.
  if (/[-–—]/.test(cleaned)) return null;
  const m = /^(\d+(?:\.\d+)?)([KMB])?$/.exec(cleaned);
  if (!m) return null;
  const n = parseFloat(m[1]);
  const suffix = m[2] as keyof typeof MULTIPLIER | undefined;
  const mult = suffix ? MULTIPLIER[suffix] : n < 1_000 ? 1_000_000 : 1;
  return Math.round(n * mult);
}

/**
 * What to put in an input box for a stored amount. The compact form ("$3M")
 * only when it parses back to exactly the same dollars; otherwise the exact
 * form, because "$2M" for a stored $1,999,000 would change it on the next save.
 */
export function moneyInputValue(stored: string | number | null | undefined): string {
  if (stored === null || stored === undefined || stored === "") return "";
  const dollars = Math.round(Number(stored));
  if (!Number.isFinite(dollars)) return String(stored);
  const short = compact(dollars);
  return parseTyped(short) === dollars ? short : exact(dollars);
}

export function parseMoney(raw: string | number | null | undefined, kind: MoneyKind): ParsedMoney {
  if (raw === null || raw === undefined) return EMPTY;
  let dollars: number | null;
  if (typeof raw === "number") {
    if (!Number.isFinite(raw) || raw < 0) return INVALID;
    dollars = Math.round(raw);
  } else {
    if (!raw.trim()) return EMPTY;
    dollars = parseTyped(raw);
    if (dollars === null) return INVALID;
  }

  const { min, max, label } = MONEY_RANGES[kind];
  const base = { dollars, display: compact(dollars), full: exact(dollars) };
  if (dollars >= min && dollars <= max) {
    return { ...base, status: "ok", suggestion: null, message: null };
  }

  const fits = [1_000, 1 / 1_000, 1_000_000, 1 / 1_000_000]
    .map((f) => Math.round(dollars! * f))
    .filter((d) => d >= min && d <= max);
  if (fits.length === 1) {
    const s = { dollars: fits[0], display: compact(fits[0]) };
    return {
      ...base,
      status: "implausible",
      suggestion: s,
      message: `${base.display} looks off for ${label}. Did you mean ${s.display}?`,
    };
  }
  return {
    ...base,
    status: "implausible",
    suggestion: null,
    message: `${base.display} is outside the usual range for ${label} (${compact(min)} to ${compact(max)}). Add K, M or B to make the unit clear.`,
  };
}
